import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Menu, X, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import NotificationsBell from "@/components/NotificationsBell";
import NonprofitBadge from "@/components/NonprofitBadge";
import api from "@/lib/api";

// Simple horizontal nav link — kept for top-level items that stay flat
// (Discover, Inbox, Dashboard). Badge slot supports the inbox unread pill.
function NavLink({ to, testId, children, badge, onClick }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      onClick={onClick}
      className="text-sm hover:text-[color:var(--terracotta)] transition-colors inline-flex items-center gap-1.5 whitespace-nowrap shrink-0"
    >
      {children}
      {badge > 0 && (
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: "var(--avt-pink)", color: "#fff", minWidth: 18, textAlign: "center" }}
          data-testid={`${testId}-badge`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

// Popover-based grouping — clusters "like items" into one trigger so the
// menu stops running off the screen. Each `items` entry: {to, label,
// testId, badge?}. Trigger renders a label + chevron.
function NavGroup({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const visible = items.filter(Boolean);
  if (!visible.length) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="text-sm hover:text-[color:var(--terracotta)] transition-colors inline-flex items-center gap-1 whitespace-nowrap shrink-0"
        >
          {label}
          <ChevronDown size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-56 p-1.5 rounded-lg"
        style={{
          backgroundColor: "var(--bone)",
          borderColor: "var(--border-warm)",
        }}
      >
        <div className="flex flex-col">
          {visible.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              onClick={() => setOpen(false)}
              className="text-sm px-3 py-2 rounded-md hover:bg-[color:var(--sand)] transition-colors flex items-center justify-between gap-3"
            >
              <span>{it.label}</span>
              {it.badge > 0 && (
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                  style={{ backgroundColor: "var(--avt-pink)", color: "#fff", minWidth: 18, textAlign: "center" }}
                >
                  {it.badge > 99 ? "99+" : it.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Same grouping in the mobile drawer, but rendered inline (no popover)
// so tap-scroll-tap doesn't need any dismissal state.
function MobileGroup({ label, items, onNavigate }) {
  const visible = items.filter(Boolean);
  if (!visible.length) return null;
  return (
    <div className="pt-2">
      <div
        className="text-[10px] uppercase tracking-widest mb-2"
        style={{ color: "var(--ink-soft)" }}
      >
        {label}
      </div>
      <div className="flex flex-col gap-2 pl-2 border-l" style={{ borderColor: "var(--border-warm)" }}>
        {visible.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            data-testid={`mobile-${it.testId}`}
            onClick={onNavigate}
            className="text-sm inline-flex items-center gap-2"
          >
            {it.label}
            {it.badge > 0 && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: "var(--avt-pink)", color: "#fff", minWidth: 18, textAlign: "center" }}
              >
                {it.badge > 99 ? "99+" : it.badge}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);

  const supporterIntent = user?.signup_intent === "volunteer" ? "/volunteer/dashboard"
    : user?.signup_intent === "donor" ? "/donor/dashboard"
    : null;
  const dashboardPath = supporterIntent
    || (user && user.role === "organizer" ? "/organizer/dashboard" : "/vendor/dashboard");
  const isAdmin = !!(user && user.is_admin);
  const isSupporter = user?.signup_intent === "volunteer" || user?.signup_intent === "donor";
  const isVendor = user?.role === "vendor" && !isSupporter;
  const isOrganizer = user?.role === "organizer" && !isSupporter;

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) { setInboxUnread(0); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/inbox/unread-count");
        if (!cancelled) setInboxUnread(data.unread_total || 0);
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.id, location.pathname]);

  // ─── Grouped nav definition ───────────────────────────────────
  // "Explore" holds every public marketing/discovery route so a fresh
  // visitor sees the platform's surface area up front.
  const exploreItems = [
    { to: "/events",    label: "Discover Events", testId: "nav-events" },
    { to: "/vendtrade", label: "VendTrade",       testId: "nav-vendtrade" },
    { to: "/vendagain", label: "VendAgain",       testId: "nav-vendagain" },
    { to: "/tourtalk",  label: "TourTalk",        testId: "nav-tourtalk" },
    { to: "/give-back", label: "Give Back",       testId: "nav-give-back-public" },
    { to: "/pricing",   label: "Pricing",         testId: "nav-pricing" },
  ];

  // "My work" is the logged-in-user's day-to-day tools: profile,
  // calendar, favorites, verification, etc. Contents shift by role.
  const workItems = [
    isVendor    && { to: "/vendor/profile",   label: "My Profile",    testId: "nav-profile" },
    (isVendor || isOrganizer) && { to: "/calendar", label: "Calendar", testId: "nav-calendar" },
    isVendor    && { to: "/vendor/bookings",  label: "Bookings",      testId: "nav-bookings" },
    isVendor    && { to: "/vendor/favorites", label: "Favorites",     testId: "nav-favorites" },
    isOrganizer && { to: "/vendors",          label: "Find vendors",  testId: "nav-browse-vendors" },
    user && !isSupporter && { to: "/alerts",  label: "Alerts",        testId: "nav-alerts" },
    isOrganizer && { to: "/organizer/verify", label: "Get Verified",  testId: "nav-organizer-verify" },
    isVendor    && { to: "/vendor/verify",    label: "Get Verified",  testId: "nav-vendor-verify" },
    user?.nonprofit_status === "verified" && {
      to: "/nonprofit/dashboard", label: "My Give Back", testId: "nav-give-back",
    },
  ].filter(Boolean);

  // "Admin" — every admin-only tool lives here. Only shown when
  // is_admin is true, so mortals never see it.
  const adminItems = isAdmin ? [
    { to: "/admin/inbox",         label: "Messages",      testId: "nav-admin-inbox" },
    { to: "/admin/users",         label: "Users",         testId: "nav-users" },
    { to: "/admin/analytics",     label: "Analytics",     testId: "nav-analytics" },
    { to: "/admin/moderation",    label: "Moderation",    testId: "nav-moderation" },
    { to: "/admin/verifications", label: "Verifications", testId: "nav-verifications" },
    { to: "/admin/promo-codes",   label: "Promos",        testId: "nav-promo-codes" },
    { to: "/admin/reconcile",     label: "Reconcile",     testId: "nav-reconcile" },
    { to: "/admin/team",          label: "Team",          testId: "nav-admin-team" },
    { to: "/admin/launch-video",  label: "Launch video",  testId: "nav-launch-video" },
  ] : [];

  // ─── Desktop nav (horizontal, scrollable if it overflows) ────
  const desktopNav = (
    <>
      <NavGroup label="Explore" items={exploreItems} testId="nav-group-explore" />
      {workItems.length > 0 && (
        <NavGroup label="My work" items={workItems} testId="nav-group-work" />
      )}
      {user && (
        <NavLink to="/inbox" testId="nav-inbox" badge={inboxUnread}>Inbox</NavLink>
      )}
      {user && <NavLink to={dashboardPath} testId="nav-dashboard">Dashboard</NavLink>}
      {adminItems.length > 0 && (
        <NavGroup label="Admin" items={adminItems} testId="nav-group-admin" />
      )}
    </>
  );

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 flex items-center gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 shrink-0">
          <img
            src="/brand/addvendtour-logo.png"
            alt="AddVendTour — Add your event. Book vendors. Discover more."
            className="h-11 sm:h-14 w-auto shrink-0"
            style={{ objectFit: "contain" }}
          />
          <span
            data-testid="brand-slogan"
            className="hidden 2xl:inline text-sm"
            style={{ color: "var(--avt-purple)", fontWeight: 700 }}
          >
            <span style={{ color: "var(--avt-teal)" }}>Add your event.</span>{" "}
            <span style={{ color: "var(--avt-pink)" }}>Book vendors.</span>{" "}
            <span style={{ color: "var(--avt-lime)" }}>Discover more.</span>
          </span>
        </Link>

        {/* Desktop nav — grouped into Explore / My work / Admin popovers so
            the header stays on a single row. `overflow-x-auto` provides a
            fallback scroll if a future addition ever overflows again. */}
        <nav
          data-testid="desktop-nav"
          className="hidden md:flex items-center gap-6 flex-1 min-w-0 overflow-x-auto no-scrollbar"
          style={{ scrollbarWidth: "none" }}
        >
          {desktopNav}
        </nav>

        <div className="flex items-center gap-3 shrink-0 ml-auto">
          {user ? (
            <>
              <NotificationsBell />
              {user.plan === "pro" && (
                <span
                  data-testid="header-plan-pro"
                  className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: "rgba(31,93,52,0.15)", color: "var(--terracotta)" }}
                  title="Maker Pro"
                >
                  Pro
                </span>
              )}
              <NonprofitBadge user={user} size="sm" />
              <span className="hidden xl:flex items-center gap-2 text-sm" data-testid="header-user">
                <User className="h-4 w-4" /> {user.name}
              </span>
              <Button variant="ghost" size="sm" data-testid="logout-btn" className="hidden sm:inline-flex"
                      onClick={async () => { await logout(); navigate("/"); }}>
                <LogOut className="h-4 w-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="hidden sm:inline text-sm hover:text-[color:var(--terracotta)] transition-colors">Log in</Link>
              <Link to="/register" data-testid="header-register" className="hidden sm:inline btn-primary text-sm px-5 py-2 rounded-full">Join</Link>
            </>
          )}

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 -mr-2 rounded-full hover:bg-[color:var(--sand)]"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer — same grouping, flattened for tap-through.
          Whole drawer is scrollable so it works on short viewports even
          when every admin section is expanded. */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t"
          style={{ borderColor: "var(--border-warm)", backgroundColor: "var(--bone)" }}
        >
          <nav className="px-6 py-5 flex flex-col gap-1 max-h-[calc(100vh-88px)] overflow-y-auto">
            <MobileGroup label="Explore" items={exploreItems} onNavigate={() => setMobileOpen(false)} />
            {workItems.length > 0 && (
              <MobileGroup label="My work" items={workItems} onNavigate={() => setMobileOpen(false)} />
            )}
            {user && (
              <div className="pt-2">
                <div
                  className="text-[10px] uppercase tracking-widest mb-2"
                  style={{ color: "var(--ink-soft)" }}
                >
                  Personal
                </div>
                <div className="flex flex-col gap-2 pl-2 border-l" style={{ borderColor: "var(--border-warm)" }}>
                  <NavLink to="/inbox" testId="mobile-nav-inbox" badge={inboxUnread} onClick={() => setMobileOpen(false)}>
                    Inbox
                  </NavLink>
                  <NavLink to={dashboardPath} testId="mobile-nav-dashboard" onClick={() => setMobileOpen(false)}>
                    Dashboard
                  </NavLink>
                </div>
              </div>
            )}
            {adminItems.length > 0 && (
              <MobileGroup label="Admin" items={adminItems} onNavigate={() => setMobileOpen(false)} />
            )}
            {user ? (
              <>
                <div className="pt-3 mt-3 border-t flex items-center gap-2 text-sm" style={{ borderColor: "var(--border-warm)", color: "var(--ink-soft)" }}>
                  <User className="h-4 w-4" /> {user.name}
                  {user.plan === "pro" && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: "rgba(31,93,52,0.15)", color: "var(--terracotta)" }}>
                      Pro
                    </span>
                  )}
                </div>
                <Button variant="outline" size="sm" data-testid="mobile-logout-btn"
                        onClick={async () => { await logout(); setMobileOpen(false); navigate("/"); }}
                        className="rounded-full self-start mt-2">
                  <LogOut className="h-4 w-4 mr-1" /> Logout
                </Button>
              </>
            ) : (
              <div className="pt-3 mt-3 border-t flex flex-col gap-3" style={{ borderColor: "var(--border-warm)" }}>
                <Link to="/login" data-testid="mobile-login" className="text-sm">Log in</Link>
                <Link to="/register" data-testid="mobile-register" className="btn-primary text-sm px-5 py-2 rounded-full inline-block text-center">Join</Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
